import React, { FocusEventHandler, KeyboardEventHandler, MouseEventHandler } from 'react';
import IconButton from '@material-ui/core/IconButton';
import { TooltipProps } from '@material-ui/core/Tooltip/Tooltip';
import styled from 'styled-components';
import * as tokens from '@utmost.co/utmost-design-tokens';
import { Tooltip } from '../Tooltip/Tooltip';
import { Uds } from '../../types';
import { SupportedIconNames, SystemIcon, SystemIconProps } from './SystemIcon';

type ButtonSize = 'x-small' | 'small' | 'large' | 'medium' | undefined;

export type ButtonColor = 'active' | 'default' | string;

export type SystemIconButtonProps = Uds<
  Omit<SystemIconProps, 'name' | 'size' | 'color'> & {
    iconName: SupportedIconNames; // SystemIconProps['name'];
    onClick?: MouseEventHandler<HTMLButtonElement>;
    onClickCapture?: MouseEventHandler<HTMLButtonElement>;
    onMouseDown?: MouseEventHandler<HTMLButtonElement>;
    onBlur?: FocusEventHandler<HTMLButtonElement>;
    onKeyDown?: KeyboardEventHandler<HTMLButtonElement>;
    disabled?: boolean;
    tooltipText?: string;
    disabledToolTipText?: string;
    tooltipPlacement?: TooltipProps['placement'];
    tabIndex?: number;
    children?: React.ReactChild;
    buttonId?: string;
    size?: ButtonSize;
    color?: ButtonColor;
  }
>;

export const SystemIconButton = React.forwardRef<HTMLButtonElement, SystemIconButtonProps>(
  (
    {
      iconName,
      color,
      className,
      style,
      size = 'medium',
      tooltipText,
      tooltipPlacement,
      dataTest,
      children,
      disabledToolTipText,
      buttonId,
      ...rest
    },
    ref
  ) => {
    const iconStyle = { opacity: rest.disabled ? 0.55 : 1 };
    return (
      <Tooltip
        title={rest.disabled ? (disabledToolTipText ?? '') : (tooltipText ?? '')}
        placement={tooltipPlacement}
        disabled={disabledToolTipText ? false : rest.disabled || !tooltipText}
        compact={false}
      >
        <StyledIconButton
          $size={size}
          color={color === 'active' ? 'primary' : undefined}
          className={className}
          style={style}
          data-test={dataTest}
          {...rest}
          id={buttonId}
          ref={ref}
        >
          <SystemIcon
            name={iconName}
            size={getIconSize(size)}
            color={getIconColor(color)}
            style={iconStyle}
            dataTest={`icon__${dataTest}`}
          />
          {children}
        </StyledIconButton>
      </Tooltip>
    );
  }
);

const StyledIconButton = styled(IconButton)<{ $size: ButtonSize }>`
  &.MuiIconButton-root {
    padding: ${({ $size }) => getButtonPadding($size)}px;
  }
`;

const getIconColor = (color: string | undefined): string | undefined => {
  if (color === 'active') return tokens.colorPrimary;
  if (color === 'default') return undefined;
  return color;
};

const getButtonPadding = (size: ButtonSize): string => {
  if (size === 'x-small') return tokens.source.spacingXxs;
  return tokens.source.spacingXs;
};

const getIconSize = (size: ButtonSize): number => {
  if (size === 'x-small') return 16;
  if (size === 'small') return 20;
  if (size === 'medium') return 24;
  return 32;
};
